import {
  AbsurdIpfError,
  IpfError,
} from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type EdgeEntityData,
  type Filters,
  type IntentRule,
  isNonNullable,
} from '@ipfabric/utils';
import type { DeviceViewEdgeEntity } from 'components/DeviceView/deviceViewTypes';
import { hasEntityAppliedCustomIntentCheck } from 'components/DeviceView/helpers/hasEntityAppliedCustomIntentCheck';
import {
  ADDRESS_FAMILIES,
  ARP_TABLE,
  BGP_NEIGHBORS,
  CDP_LLDP_ALL_NEIGHBORS,
  CLOUD_NETWORK_PEERINGS,
  EIGRP_INTERFACES,
  EIGRP_NEIGHBORS,
  FEX_INTERFACES,
  FEX_MODULES,
  INTERFACES,
  INVENTORY_HOSTS,
  ISIS_INTERFACES,
  ISIS_NEIGHBORS,
  LDP_INTERFACES,
  LDP_NEIGHBORS,
  MANAGED_IP,
  MANAGED_IP_IPV6,
  MTU,
  OSPFV3_INTERFACES,
  OSPFV3_NEIGHBORS,
  OSPF_INTERFACES,
  OSPF_NEIGHBORS,
  PIM_INTERFACES,
  PIM_NEIGHBORS,
  type ProtocolTableFilterColumns,
  RIP_INTERFACES,
  RIP_NEIGHBORS,
  ROUTES,
  ROUTES_IPV6,
  RSVP_INTERFACES,
  RSVP_NEIGHBORS,
  STP_NEIGHBORS,
  SWITCHPORT,
  VIRTUAL_PORTS,
  VXLAN_PEERS,
  WIRELESS_ACCESS_POINTS,
  WIRELESS_CLIENTS,
  tableFiltersAttributes,
  tablesInProtocols,
} from 'components/DeviceView/protocolTables';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import deepEqual from 'react-fast-compare';
import { isProtocolStpi } from '../../../helpers/isProtocolStpi';
import { isSourceGroupNode } from '../../../helpers/isSourceGroupNode';
import { isTargetGroupNode } from '../../../helpers/isTargetGroupNode';
import { isWlcApEdge } from '../../../helpers/isWlcApEdge';
import {
  type AttributeFilter,
  createAddressFilter,
  createCustomSnFilter,
  createIntNameFilter,
  createInventoryHostsChildIntNameFilter,
  createInventoryHostsSnFilter,
  createNexthopFilter,
  createSnFilter,
  createVlanIdFilter,
} from './edgeTableAttributeFilters';

import { isTargetGroupMemberNode } from '../../../helpers/isTargetGroupMemberNode';

interface EdgeDefaultFilter {
  and: AttributeFilter[];
}

interface EdgeStpiVirtualPortsFilter {
  or: { and: AttributeFilter[] }[];
}

type EdgeFilter = EdgeDefaultFilter | EdgeStpiVirtualPortsFilter;

interface DefaultTableFilters {
  type: 'default';
  filters: ((
    edge: EdgeEntityData,
    filterColumns: ProtocolTableFilterColumns,
  ) => AttributeFilter | null)[];
  oppositeDirectionFilters?: ((
    edge: EdgeEntityData,
    filterColumns: ProtocolTableFilterColumns,
  ) => AttributeFilter | null)[];
}

interface StpiVirtualPortsTableFilters {
  type: 'stpiVirtualPorts';
  sourceFilters: ((
    edge: EdgeEntityData,
    filterColumns: ProtocolTableFilterColumns,
  ) => AttributeFilter | null)[];
  targetFilters: ((
    edge: EdgeEntityData,
    filterColumns: ProtocolTableFilterColumns,
  ) => AttributeFilter | null)[];
}

type TableFilters = DefaultTableFilters | StpiVirtualPortsTableFilters;

const tableFiltersIsDefaultTableFilters = (
  tableFilters: TableFilters,
): tableFilters is DefaultTableFilters => tableFilters.type === 'default';

const tableFiltersIsStpiVirtualPortsTableFilters = (
  tableFilters: TableFilters,
): tableFilters is StpiVirtualPortsTableFilters =>
  tableFilters.type === 'stpiVirtualPorts';

const filterUniqueEdgeFilters = (
  edgeFilter: EdgeFilter,
  index: number,
  self: EdgeFilter[],
) => index === self.findIndex((obj: EdgeFilter) => deepEqual(obj, edgeFilter));

const filterNonNullAttributes = (
  item: AttributeFilter | null,
): item is AttributeFilter => item !== null;

const createTableFilters =
  (
    edges: EdgeEntityData[],
    selectedTable: string,
    protocolTableFilterColumns: ProtocolTableFilterColumns,
    deviceViewEntity: DeviceViewEdgeEntity,
    intentRule: IntentRule | null,
  ) =>
  (tableFilters: TableFilters): Filters => {
    const filterOutInvalidProtocols = (edge: EdgeEntityData): boolean => {
      if (edge.protocol === undefined) {
        // e.g. path lookup edges do not have protocols, still we want to display tables (e.g. intent results) for them
        return true;
      }

      if (!tablesInProtocols[edge.protocol]) {
        return false;
      }
      // Edge with intent check can display any table
      // Check if table has filters definition
      if (
        hasEntityAppliedCustomIntentCheck(deviceViewEntity.intentCheckResult) &&
        intentRule?.webEndpoint !== undefined
      ) {
        return tableFiltersAttributes[intentRule.webEndpoint] !== undefined;
      }
      return tablesInProtocols[edge.protocol]!.includes(selectedTable);
    };

    const getEdgeTableFilter = (edge: EdgeEntityData): EdgeFilter => {
      if (tableFiltersIsStpiVirtualPortsTableFilters(tableFilters)) {
        if (!isProtocolStpi(edge.protocol)) {
          throw new IpfError(
            'INVALID_ARGUMENT',
            'Invalid protocol. Expected stpi or stpi-blocked',
          );
        }
        return {
          or: [
            {
              and: tableFilters.sourceFilters
                .map((filter) => filter(edge, protocolTableFilterColumns))
                .filter(filterNonNullAttributes),
            },
            {
              and: tableFilters.targetFilters
                .map((filter) => filter(edge, protocolTableFilterColumns))
                .filter(filterNonNullAttributes),
            },
          ],
        };
      }
      if (tableFiltersIsDefaultTableFilters(tableFilters)) {
        const filters = {
          and: tableFilters.filters
            .map((filter) => filter(edge, protocolTableFilterColumns))
            .filter(filterNonNullAttributes),
        };
        if (
          deviceViewEntity.edgeDirection === 'bidirected' &&
          isNonNullable(tableFilters.oppositeDirectionFilters)
        ) {
          return {
            or: [
              filters,
              {
                and: tableFilters.oppositeDirectionFilters
                  .map((filter) => filter(edge, protocolTableFilterColumns))
                  .filter(filterNonNullAttributes),
              },
            ],
          };
        }
        return filters;
      }

      throw new AbsurdIpfError(tableFilters, 'unknown table filter type');
    };

    const edgesFilters: Filters = pipe(
      edges,
      array.filter(filterOutInvalidProtocols),
      array.map(getEdgeTableFilter),
      // unfortunately cannot use fp-ts array.filter as it uses other ArrayPrototype.filter() arguments
      (transformedEdges) => transformedEdges.filter(filterUniqueEdgeFilters),
      (transformedEdges) =>
        transformedEdges.length === 1
          ? // don't add 'or' filter layer if graph edge contains only 1 edge/protocol
            transformedEdges[0]
          : { or: transformedEdges },
    ) as unknown as Filters;

    return edgesFilters;
  };

const getCloudNetworkPeeringsFilters = (
  deviceViewEntity: DeviceViewEdgeEntity,
): Filters => {
  if (!deviceViewEntity.sourceSn || !deviceViewEntity.targetSn) {
    return {};
  }
  return {
    or: [
      {
        and: [
          { cloudNetworkId: ['eq', deviceViewEntity.sourceSn] },
          { remoteNetworkId: ['eq', deviceViewEntity.targetSn] },
        ],
      },
      {
        and: [
          { cloudNetworkId: ['eq', deviceViewEntity.targetSn] },
          { remoteNetworkId: ['eq', deviceViewEntity.sourceSn] },
        ],
      },
    ],
  };
};

export const getEdgeFilters = (
  deviceViewEntity: DeviceViewEdgeEntity,
  selectedTable: string,
  intentRule: IntentRule | null,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD cleanup eventually
): Filters => {
  if (selectedTable === CLOUD_NETWORK_PEERINGS) {
    return getCloudNetworkPeeringsFilters(deviceViewEntity);
  }
  const protocolTableFilterColumns = tableFiltersAttributes[selectedTable];
  if (!protocolTableFilterColumns) {
    return {};
  }
  const getTableFilters = createTableFilters(
    deviceViewEntity.edges!,
    selectedTable,
    protocolTableFilterColumns,
    deviceViewEntity,
    intentRule,
  );
  switch (selectedTable) {
    case MTU:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createIntNameFilter('source'),
          createSnFilter('target'),
          createIntNameFilter('target'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
          createIntNameFilter('target', 'eq', true),
        ],
      });
    case ADDRESS_FAMILIES:
    case BGP_NEIGHBORS:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createAddressFilter('source'),
          createSnFilter('target'),
          createAddressFilter('target'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createAddressFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
          createAddressFilter('target', 'eq', true),
        ],
      });
    case ROUTES:
    case ROUTES_IPV6:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createNexthopFilter('source', 'like'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createNexthopFilter('source', 'like', true),
        ],
      });
    case OSPFV3_NEIGHBORS:
    case VXLAN_PEERS:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createIntNameFilter('source'),
          createSnFilter('target'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
        ],
      });
    case EIGRP_NEIGHBORS:
    case ISIS_NEIGHBORS:
    case LDP_NEIGHBORS:
    case OSPF_NEIGHBORS:
    case PIM_NEIGHBORS:
    case RIP_NEIGHBORS:
    case RSVP_NEIGHBORS:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createIntNameFilter('source'),
          createSnFilter('target'),
          createAddressFilter('target'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
          createAddressFilter('target', 'eq', true),
        ],
      });
    case CDP_LLDP_ALL_NEIGHBORS:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createIntNameFilter('source'),
          createSnFilter('target'),
          createIntNameFilter('target'),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
          createIntNameFilter('target', 'eq', true),
        ],
      });
    case ARP_TABLE:
    case EIGRP_INTERFACES:
    case INTERFACES:
    case ISIS_INTERFACES:
    case LDP_INTERFACES:
    case MANAGED_IP:
    case MANAGED_IP_IPV6:
    case OSPFV3_INTERFACES:
    case OSPF_INTERFACES:
    case PIM_INTERFACES:
    case RIP_INTERFACES:
    case SWITCHPORT:
    case RSVP_INTERFACES:
      return getTableFilters({
        type: 'default',
        filters: [createSnFilter('source'), createIntNameFilter('source')],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
        ],
      });
    case VIRTUAL_PORTS:
      if (isProtocolStpi(deviceViewEntity.protocol)) {
        return getTableFilters({
          type: 'stpiVirtualPorts',
          sourceFilters: [
            createSnFilter('source'),
            createIntNameFilter('source'),
            createVlanIdFilter(),
          ],
          targetFilters: [
            createSnFilter('target'),
            createIntNameFilter('target'),
            createVlanIdFilter(),
          ],
        });
      }
      return getTableFilters({
        type: 'default',
        filters: [createSnFilter('source'), createIntNameFilter('source')],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createIntNameFilter('source', 'eq', true),
        ],
      });
    case STP_NEIGHBORS:
      return getTableFilters({
        type: 'default',
        filters: [
          createSnFilter('source'),
          createSnFilter('target'),
          createVlanIdFilter(),
        ],
        oppositeDirectionFilters: [
          createSnFilter('source', 'eq', true),
          createSnFilter('target', 'eq', true),
          createVlanIdFilter(),
        ],
      });
    case FEX_MODULES:
      return getTableFilters({
        type: 'default',
        filters: [createSnFilter('target')],
        oppositeDirectionFilters: [createSnFilter('target', 'eq', true)],
      });
    case FEX_INTERFACES:
      return getTableFilters({
        type: 'default',
        filters: [createSnFilter('target'), createIntNameFilter('target')],
        oppositeDirectionFilters: [
          createSnFilter('target', 'eq', true),
          createIntNameFilter('target', 'eq', true),
        ],
      });
    case INVENTORY_HOSTS: {
      if (
        isTargetGroupNode(deviceViewEntity) ||
        isTargetGroupMemberNode(deviceViewEntity)
      ) {
        return getTableFilters({
          type: 'default',
          filters: [createInventoryHostsSnFilter('source')],
          oppositeDirectionFilters: [
            createInventoryHostsSnFilter('source', 'eq', true),
          ],
        });
      }
      if (isSourceGroupNode(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [
            createSnFilter('target'),
            createInventoryHostsChildIntNameFilter('target'),
          ],
          oppositeDirectionFilters: [
            createSnFilter('target', 'eq', true),
            createInventoryHostsChildIntNameFilter('target', 'eq', true),
          ],
        });
      }
      stumble('No defined filter for inventory hosts table');
      return {};
    }
    case WIRELESS_ACCESS_POINTS: {
      if (isWlcApEdge(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [
            createCustomSnFilter('source', 'controllerSn', 'sn'),
            createCustomSnFilter('target', 'apSn', 'sn'),
          ],
          oppositeDirectionFilters: [
            createCustomSnFilter('target', 'controllerSn', 'sn'),
            createCustomSnFilter('source', 'apSn', 'sn'),
          ],
        });
      }
      if (isTargetGroupNode(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [createCustomSnFilter('source', 'apSn', 'sn')],
          oppositeDirectionFilters: [
            createCustomSnFilter('target', 'apSn', 'sn'),
          ],
        });
      }
      // Child node table not available
      stumble('Edge has no defined filters for access points');
      return {};
    }
    case WIRELESS_CLIENTS: {
      if (isWlcApEdge(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [
            createCustomSnFilter('source', 'controllerSn', 'sn'),
            createCustomSnFilter('target', 'apSn', 'sn'),
          ],
          oppositeDirectionFilters: [
            createCustomSnFilter('target', 'controllerSn', 'sn'),
            createCustomSnFilter('source', 'apSn', 'sn'),
          ],
        });
      }
      if (isTargetGroupNode(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [createCustomSnFilter('source', 'apSn', 'sn')],
          oppositeDirectionFilters: [
            createCustomSnFilter('target', 'apSn', 'sn'),
          ],
        });
      }
      if (isSourceGroupNode(deviceViewEntity)) {
        return getTableFilters({
          type: 'default',
          filters: [createCustomSnFilter('target', 'uniqId', 'sn')],
          oppositeDirectionFilters: [
            createCustomSnFilter('source', 'uniqId', 'sn'),
          ],
        });
      }
      stumble('Edge has no defined filters for wireless clients');
      return {};
    }
    default:
      return {};
  }
};
